import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { FC } from '.'
import { queryClient } from '../App'

const time = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000
}

const queryConfig = {
  rare: {
    staleTime: 1 * time.hours,
    gcTime: 2 * time.hours
  },
  frequent: {
    staleTime: 5 * time.minutes,
    gcTime: 10 * time.minutes
  },
  autoFetchFrequent: {
    staleTime: 3 * time.minutes,
    gcTime: 5 * time.minutes,
    refetchInterval: 2 * time.minutes
  }
}

const queryNotFound = {
  config: {},
  queryFn: (key) => { throw new Error('Query Not Found: ' + key) }
}

const queries = {
  users: {
    config: queryConfig.rare,
    queryFn: () => FC.service('users').find({})
  },
  user: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('users').get(id)
      return res
    }
  },
  agencies: {
    config: queryConfig.rare,
    queryFn: () => FC.service('agencies').find({})
  },
  agency: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('agencies').get(id)
      return res
    }
  },
  servers: {
    config: queryConfig.rare,
    queryFn: () => FC.service('servers').find({})
  },
  configs: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, query] }) => {
      if (!query) {
        const res = await FC.service('configs').find({})
        return res
      }
      const res = await FC.service('configs').find({ query: { ...query } })
      return res
    }
  },
  config: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('configs').get(id)
      return res
    }
  },
  client: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('clients').get(id)
      return res
    }
  },
  clients: {
    config: queryConfig.rare,
    queryFn: () => FC.service('clients').find({})
  },
  clientsByAgency: {
    config: queryConfig.rare,
    queryFn: ({ queryKey }) => {
      const agencyId = queryKey[1]
      return FC.service('clients').find({
        query: { agencyId }
      })
    }
  },
  reports: {
    config: queryConfig.frequent,
    queryFn: () => FC.service('reports').find({})
  },
  report: {
    config: queryConfig.frequent,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') {
        return {
          _id: 'new',
          name: '',
          reportsList: [],
          reportFilter: { year: new Date().getFullYear(), months: { start: 0, end: 11 }, clients: [], filterClients: false /* tipoBiglietteria: '' */ }
        }
      }
      const [res] = await FC.service('reports').find({ query: { _id: id } })
      return res
    }
  },
  process: {
    config: queryConfig.frequent,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('processes').get(id)
      return res
    }
  },
  processesPage: {
    config: queryConfig.frequent,
    queryFn: async ({ queryKey: [, query = {}] }) => {
      const res = await FC.service('processes').find({ query })
      return res
    }
  },
  processesErrorCount: {
    config: queryConfig.frequent,
    queryFn: async ({ queryKey: [, query = {}] }) => {
      const res = await FC.service('processes').find({
        query: {
          ...query,
          $or: [{ 'retryErrors.0': { $exists: true } }, { ftpError: { $ne: null } }, { status: 'error' }],
          $limit: 0
        }
      })
      return res?.total || 0
    }
  },
  logs: {
    config: {
      staleTime: 30 * time.seconds,
      gcTime: 2 * time.minutes
    },
    queryFn: async ({ queryKey: [, params = {}] }) => {
      const res = await FC.service('logs').find({ query: { ...params } })
      return res
    },
    noInitalPrefetch: true
  },
  logArchives: {
    config: queryConfig.frequent,
    queryFn: () => FC.service('log-archives').find({}),
    noInitalPrefetch: true
  },
  restoredLogs: {
    config: {
      staleTime: 30 * time.seconds,
      gcTime: 2 * time.minutes
    },
    queryFn: async ({ queryKey: [, params = {}] }) => {
      const res = await FC.service('restored-logs').find({ query: { ...params } })
      return res
    },
    noInitalPrefetch: true
  },
  restoreLogBatches: {
    config: queryConfig.frequent,
    queryFn: async () => {
      const res = await FC.service('restored-logs').find({ query: { listBatches: true } })
      return res
    },
    noInitalPrefetch: true
  },
  blacklist: {
    config: queryConfig.rare,
    queryFn: () => FC.service('blacklist').find({})
  },
  maintenance: {
    config: { staleTime: 30 * time.seconds, gcTime: 2 * time.minutes },
    queryFn: () => FC.service('maintenance').find({}),
    noInitalPrefetch: true
  },
  appSettings: {
    config: { staleTime: 30 * time.seconds, gcTime: 2 * time.minutes },
    queryFn: () => FC.service('app-settings').find({}),
    noInitalPrefetch: true
  }
}

export const useCQuery = (queryArgs, extraQueries = [], queryOptions = {}) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const { queryFn, config } = { ...queries, ...extraQueries }?.[queryKey[0]] || queryNotFound
  return useQuery({ queryKey, queryFn, ...config, ...queryOptions })
}

export const prefetchQuery = (queryKey, extraQueries = []) => {
  const { queryFn, config } = { ...queries, ...extraQueries }[Array.isArray(queryKey) ? queryKey[0] : queryKey] || queryNotFound
  queryClient.prefetchQuery(queryKey, queryFn, config)
}

export const prefetchQueries = (extraQueries) =>
  Object.entries({ ...queries, ...extraQueries }).forEach(([queryKey, { config, queryFn, defaultKeys = [], noInitalPrefetch }]) => {
    const key = [queryKey, ...defaultKeys]
    !noInitalPrefetch && queryClient.prefetchQuery(key, queryFn, config)
  })

export const invalidateQuery = (queryKeys) =>
  (Array.isArray(queryKeys) ? queryKeys : [queryKeys])
    .forEach(queryKey => queryClient.invalidateQueries({ queryKey: [queryKey] }))

// FETCH QUERY

export const fetchQuery = (queryArgs, extraQueries = []) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const { queryFn, config } = { ...queries, ...extraQueries }?.[queryKey[0]] || queryNotFound
  return queryClient.fetchQuery({ queryKey, queryFn, ...config })
}

// INFINITE QUERIES
const infiniteQueries = {
  documents: {
    firstPageLength: 100,
    pageLength: 100,
    config: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true
    },
    queryFn: (firstPageLength, pageLength) => ({ pageParam = 1, queryKey: [, query = {}] = [] }) =>
      FC.client.service('documents').find({
        query:
        {
          $skip: (pageParam === 1 ? 0 : ((pageParam - 2) * pageLength + firstPageLength)),
          $limit: pageParam === 1 ? firstPageLength : pageLength,
          $sort: { createdAt: -1 },
          ...query
        }
      }),
    noInitalPrefetch: true
  },
  processes: {
    firstPageLength: 200,
    pageLength: 200,
    config: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true
    },
    queryFn: (firstPageLength, pageLength) => ({ pageParam = 1, queryKey: [, query = {}] = [] }) =>
      FC.service('processes').find({
        query: {
          $skip: (pageParam === 1 ? 0 : ((pageParam - 2) * pageLength + firstPageLength)),
          $limit: pageParam === 1 ? firstPageLength : pageLength,
          $sort: { createdAt: -1 },
          ...query
        }
      }),
    noInitalPrefetch: true
  }
}

export const useCInfiniteQuery = (queryArgs, extraQueries = [], queryOptions = {}) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const { queryFn, config, firstPageLength = 50, pageLength = 50 } = { ...infiniteQueries, ...extraQueries }?.[queryKey[0]] || queryNotFound

  const { data = {}, isSuccess, fetchNextPage, isFetching, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: queryFn(firstPageLength, pageLength),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      (pages?.length === 1)
        ? ((Array.isArray(lastPage) ? lastPage?.length : lastPage?.data?.length) === firstPageLength) ? pages?.length + 1 : null
        : ((Array.isArray(lastPage) ? lastPage?.length : lastPage?.data?.length) === pageLength) ? pages?.length + 1 : null,
    ...config,
    ...queryOptions
  })

  const pages = data?.pages?.map(page => Array.isArray(page) ? page : page.data).flat()

  useEffect(() => { 
    !isFetching && !isFetchingNextPage && hasNextPage && fetchNextPage() 
  }, [isFetchingNextPage, isFetching, hasNextPage, fetchNextPage])

  return { data: pages, isSuccess, isFetchingNextPage }
}
